import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50 shadow-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link
              to="/"
              className="flex items-center gap-2 cursor-pointer flex-shrink-0"
              onClick={() => setMobileOpen(false)}
            >
              <span className={`font-heading text-xl md:text-2xl font-semibold tracking-tight transition-colors duration-300 ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}>
                vigorbuy
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}
              >
                <i className="ri-home-4-line mr-1"></i>Home
              </Link>
              <Link
                to="/shopping-guide"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}
              >
                <i className="ri-book-open-line mr-1"></i>Shopping Guide
              </Link>
              <a
                href="#about"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}
              >
                About
              </a>
              <a
                href="#categories"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}
              >
                Categories
              </a>
              <a
                href="#guides"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}
              >
                Guides
              </a>
              <a
                href="#safety"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}
              >
                Safety
              </a>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer bg-transparent border-none ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}
                >
                  Shop by Category
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-background-50 rounded-lg border border-background-200/80 py-2 shadow-lg max-h-80 overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/shopping-guide"
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer border ${scrolled ? 'border-foreground-300 text-foreground-700 hover:bg-foreground-50' : 'border-background-50/40 text-background-50 hover:bg-background-50/10'}`}
              >
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                vigorbuy sheet
                <i className="ri-arrow-right-up-line ml-1.5 text-base"></i>
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden flex items-center gap-1 text-sm font-medium cursor-pointer bg-transparent border-none transition-colors duration-300 ${scrolled ? 'text-foreground-700' : 'text-background-50'}`}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <i className="ri-close-line text-2xl"></i>
              ) : (
                <i className="ri-menu-line text-2xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-background-50 pt-16 overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-2">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-base font-semibold text-foreground-900 rounded-xl hover:bg-background-100 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className="ri-home-4-line text-lg"></i>
              </div>
              Home
            </Link>
            <Link
              to="/shopping-guide"
              className="flex items-center gap-3 px-4 py-3 text-base font-semibold text-foreground-900 rounded-xl hover:bg-background-100 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-book-open-line text-lg"></i>
              </div>
              Shopping Guide
            </Link>

            <div className="border-t border-background-200/70 my-3"></div>

            <p className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Navigate</p>
            <a href="#about" className="px-4 py-2.5 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 transition-colors" onClick={() => setMobileOpen(false)}>About</a>
            <a href="#categories" className="px-4 py-2.5 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 transition-colors" onClick={() => setMobileOpen(false)}>Categories</a>
            <a href="#guides" className="px-4 py-2.5 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 transition-colors" onClick={() => setMobileOpen(false)}>Guides</a>
            <a href="#safety" className="px-4 py-2.5 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 transition-colors" onClick={() => setMobileOpen(false)}>Safety</a>

            <div className="border-t border-background-200/70 my-3"></div>

            <p className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Shop by Category</p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 hover:text-foreground-950 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </div>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="border-t border-background-200/70 my-3"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-5 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap mt-2"
              onClick={() => setMobileOpen(false)}
            >
              vigorbuy sheet
              <i className="ri-arrow-right-up-line text-lg"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-5 py-3.5 bg-foreground-950 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap"
              onClick={() => setMobileOpen(false)}
            >
              buy it now
              <i className="ri-shopping-bag-line text-lg"></i>
            </a>
          </div>
        </div>
      )}
    </>
  );
}